import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  useTheme
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import type { Payment } from "../../Graphql/types/payment";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useGetPaymentList } from "../hooks/api/usePayment";
import { getPaymentDetailPath } from "../../constants/routes";
import {
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";

const PaymentList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const [paymentList, setPaymentList] = useState<Payment[]>([]);
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const [search, setSearch] = useState("");

  const getPaymentListMutation = useGetPaymentList();

  useEffect(() => {
    getPaymentListMutation
      .mutateAsync({})
      .then((res) => setPaymentList(res.listPayments.paymentList ?? []))
      .catch(() => {});
  }, []);

  const filteredList = useMemo(() => {
    if (!search.trim()) return paymentList;
    return paymentList.filter((payment) =>
      Object.values(payment)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [paymentList, search]);

  const getStatusColor = (
    status: string
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
      case "SUCCESS":
        return "success";
      case "PENDING":
        return "warning";
      case "FAILED":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        color: "black",
        mt: 1
      }}
    >
      <Box gap={4} flexDirection="column" display="flex">
        {/* Toolbar Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          gap={2}
          flexDirection={matchMD ? "column" : "row"}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            textAlign={matchMD ? "center" : "left"}
            color="textPrimary"
          >
            {t("payment-history-title", "Payment History")}
          </Typography>

          <TextField
            placeholder={t("payment-search-placeholder", "Search payments...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ width: matchMD ? "100%" : "300px" }}
          />
        </Box>

        {/* Table Section */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("payment-col-id", "Payment ID")}</TableCell>
                <TableCell>{t("payment-col-amount", "Amount")}</TableCell>
                <TableCell>{t("payment-col-method", "Method")}</TableCell>
                <TableCell>{t("payment-col-status", "Status")}</TableCell>
                <TableCell>{t("payment-col-date", "Date")}</TableCell>
                <TableCell align="right">
                  {t("payment-col-actions", "Actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t("payment-empty", "No payments found")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredList.map((payment) => (
                  <TableRow key={payment.paymentId} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {payment.paymentId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="medium">
                        {payment.currency}{" "}
                        {payment.amount?.toFixed(2) ?? "0.00"}
                      </Typography>
                    </TableCell>
                    <TableCell>{payment.paymentMethod}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          payment.paymentStatus
                            ? payment.paymentStatus.charAt(0) +
                              payment.paymentStatus.slice(1).toLowerCase()
                            : payment.paymentStatus
                        }
                        color={getStatusColor(payment.paymentStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {payment.createdAt
                        ? new Date(payment.createdAt).toLocaleDateString()
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        component="button"
                        onClick={() =>
                          navigate(
                            localizedPath(
                              getPaymentDetailPath(payment.paymentId),
                              language,
                              defaultLang
                            )
                          )
                        }
                        sx={{
                          color: "primary",
                          cursor: "pointer",
                          border: "none",
                          background: "none",
                          textDecoration: "underline"
                        }}
                      >
                        {t("payment-view-action", "View")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default PaymentList;
